// The declared panels' rules, apart from their drawing
// (02-DESIGN/14-solutions/03-the-console.md § where a solution appears, §
// the elements): who an element is drawn for — an element calling a verb its
// role marks admin, or reading or writing the solution's tenant-scoped
// settings or secrets, only for an admin, because the broker would refuse a
// member and nothing is rendered as a permission — which actions belong to
// the list before them, and how what a verb answers becomes rows and points.
// The console draws what the fields it named hold and ignores the rest.
//
// An element whose role has no workload placed in this workspace — a role
// on grant where no grant to receive is held — is not drawn, and a panel left
// with nothing drawn for the person is not listed; a call that nothing
// answers, or that gets no answer in time, ends in words, never in a wait
// (§ the elements, hq issue 151).
//
// A panel or a setting that declares `sends` is drawn only where a grant
// line of the workspace's own blueprint sends one of the capabilities it
// names (§ the elements; hq issue 185): the grants are the caller's, read by
// sends.ts.
//
// A setting that declares `receives` is asked only where the workspace
// receives one of the capabilities it names (§ the elements; hq issue 189):
// where the install record binds a role on grant that receives one of
// them, which the solutions service does only while a grant to receive is
// held. The grants a workspace receives are lines of other workspaces'
// blueprints, which the console cannot read; the binding is the record the
// role rule already reads.

import { subject } from "@impire-io/sdk";

import { forSolution, sendsHere, type Grant } from "./sends.ts";
import { CODE_NO_ANSWER, CODE_NO_SERVICE, isRefusal, Refusal } from "../../platform/bus.ts";
import {
  Audience,
  ElementKind,
  type Capability,
  Scope,
  type Element,
  type Field,
  type Manifest,
  type Panel,
  type Setting,
} from "../../wire/solutions.ts";

/** KNOWN_ELEMENTS are the kinds this console draws; any other is *this panel needs a newer console*. */
export const KNOWN_ELEMENTS: ReadonlySet<string> = new Set(Object.values(ElementKind));

/** adminVerb reports whether the role marks the verb admin. */
export function adminVerb(m: Manifest, role: string | undefined, verb: string | undefined): boolean {
  const w = m.workloads?.find((x) => x.role === role);
  return w?.serves?.some((v) => v.verb === verb && v.admin === true) === true;
}

/** forAdminOnly reports whether an element is drawn only for an admin. */
export function forAdminOnly(m: Manifest, e: Element): boolean {
  if (e.kind === ElementKind.setting) {
    const s = m.settings?.find((x) => x.name === e.setting);
    if (s !== undefined) {
      return s.scope === Scope.tenant;
    }
    return m.secrets?.some((x) => x.name === e.setting) === true;
  }
  if (e.kind === ElementKind.chat) {
    return [e.verb, e.cancel, e.models, e.threads, e.thread, e.clear].some((v) => adminVerb(m, e.role, v));
  }
  return adminVerb(m, e.role, e.verb);
}

/** Ids is the workload id per role, from the install record. */
export type Ids = Readonly<Record<string, string>>;

/**
 * placed reports whether an element's workload is placed in this workspace:
 * one naming a role only where the install record binds the role to a
 * workload id; one naming no role — a setting — always.
 */
export function placed(e: Element, ids: Ids): boolean {
  return e.role === undefined || ids[e.role] !== undefined;
}

/**
 * drawnFor is the panel's elements a person is drawn: those whose workload is
 * placed here, and of those everything for an admin, a member's without the
 * admin's.
 */
export function drawnFor(
  m: Manifest,
  elements: readonly Element[],
  admin: boolean,
  ids: Ids,
  grants: readonly Grant[],
): Element[] {
  return elements.filter(
    (e) => placed(e, ids) && (admin || !forAdminOnly(m, e)) && settingSent(m, e, grants, ids),
  );
}

/** settingSent reports whether a setting element's setting is asked here: one declaring `sends` only where a grant sends, one declaring `receives` only where the workspace receives. */
function settingSent(m: Manifest, e: Element, grants: readonly Grant[], ids: Ids): boolean {
  if (e.kind !== ElementKind.setting) {
    return true;
  }
  const s = m.settings?.find((x) => x.name === e.setting);
  return sendsHere(s?.sends, forSolution(grants, m.name)) && receivesHere(m, s?.receives, ids);
}

/**
 * receivesHere reports whether something declaring `receives` is asked:
 * always where it declares none; otherwise only where the install record
 * binds a role on grant that receives one of the capabilities it names.
 */
export function receivesHere(m: Manifest, receives: readonly Capability[] | undefined, ids: Ids): boolean {
  if (receives === undefined || receives.length === 0) {
    return true;
  }
  return (m.workloads ?? []).some(
    (w) =>
      w.on_grant === true &&
      ids[w.role] !== undefined &&
      (w.receives ?? []).some((c) => (receives as readonly string[]).includes(c)),
  );
}

/** settingsAsked is the solution's settings asked here: those declaring `sends` only where a grant sends one of them, those declaring `receives` only where the workspace receives one of them. */
export function settingsAsked<S extends Pick<Setting, "sends" | "receives">>(
  m: Manifest,
  settings: readonly S[],
  grants: readonly Grant[],
  ids: Ids,
): S[] {
  const mine = forSolution(grants, m.name);
  return settings.filter((s) => sendsHere(s.sends, mine) && receivesHere(m, s.receives, ids));
}

/** panelSent reports whether a panel exists here: one declaring `sends` only where a grant sends one of them. */
export function panelSent(m: Manifest, p: Panel, grants: readonly Grant[]): boolean {
  return sendsHere(p.sends, forSolution(grants, m.name));
}

/**
 * forAudience reports whether a panel's audience admits the person: an
 * admin's panel an admin only; a person's panel, and one with no audience,
 * everyone (§ where a solution appears).
 */
export function forAudience(p: Panel, admin: boolean): boolean {
  return p.audience !== Audience.admin || admin;
}

function listed(m: Manifest, p: Panel, ids: Ids, admin: boolean, grants: readonly Grant[]): boolean {
  return (
    forAudience(p, admin) && panelSent(m, p, grants) && drawnFor(m, p.elements, admin, ids, grants).length > 0
  );
}

/** panelsShown is the declared panels listed under *Solutions* for a person: for their audience, not the person's own, sent where they declare `sends`, with at least one element drawn for them. */
export function panelsShown(m: Manifest, ids: Ids, admin: boolean, grants: readonly Grant[]): Panel[] {
  return (m.panels ?? []).filter((p) => p.audience !== Audience.person && listed(m, p, ids, admin, grants));
}

/** personPanels is the declared panels listed in the person's menu: the person's own, for a member and an admin alike. */
export function personPanels(m: Manifest, ids: Ids, admin: boolean, grants: readonly Grant[]): Panel[] {
  return (m.panels ?? []).filter((p) => p.audience === Audience.person && listed(m, p, ids, admin, grants));
}

/**
 * Selection is a panel's selection as the address carries it: per field set,
 * the value sent and the words its chip shows.
 */
export type Selection = Readonly<Record<string, { value: string; label: string }>>;

const LABEL = ":label";

/** selectionOf reads the panel's selection from the address's query: only the fields the panel declares. */
export function selectionOf(p: Panel, q: URLSearchParams): Selection {
  const out: Record<string, { value: string; label: string }> = {};
  for (const f of p.selection ?? []) {
    const v = q.get(f.name);
    if (v !== null && v !== "") {
      out[f.name] = { value: v, label: q.get(f.name + LABEL) ?? v };
    }
  }
  return out;
}

/** withSelected is the query with one field chosen, or removed where value is undefined; every other parameter kept. */
export function withSelected(
  q: URLSearchParams,
  field: string,
  value?: string,
  label?: string,
): URLSearchParams {
  const next = new URLSearchParams(q);
  next.delete(field);
  next.delete(field + LABEL);
  if (value !== undefined) {
    next.set(field, value);
    if (label !== undefined && label !== value) {
      next.set(field + LABEL, label);
    }
  }
  return next;
}

/** cleared is the query without any of the panel's selection. */
export function cleared(p: Panel, q: URLSearchParams): URLSearchParams {
  let next = new URLSearchParams(q);
  for (const f of p.selection ?? []) {
    next = withSelected(next, f.name);
  }
  return next;
}

/**
 * bodyOf is what a reading element sends: the panel's selection, what the
 * person filled, then the element's fixed parameters, which nothing overrides;
 * undefined where it sends nothing.
 */
export function bodyOf(
  e: Element,
  selection: Selection,
  filled?: Readonly<Record<string, unknown>>,
): Record<string, unknown> | undefined {
  const body: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(selection)) {
    body[k] = v.value;
  }
  Object.assign(body, filled ?? {}, e.params ?? {});
  return Object.keys(body).length === 0 ? undefined : body;
}

/** dayOf is a point's time as the day a chip names, `2026-10-30`. */
export function dayOf(at: string): string {
  return at.slice(0, 10);
}

/** unplaced is the refusal an element whose role runs no workload here ends in. */
export function unplaced(role: string | undefined): Refusal {
  return new Refusal(
    CODE_NO_SERVICE,
    `The solution's ${role ?? "workload"} does not run in this workspace, so there is nothing to show.`,
  );
}

/** Trouble is the words a call nothing answered ends in. */
export interface Trouble {
  title: string;
  text: string;
}

/**
 * troubleOf names a call nothing answered: *Not running here* where no
 * workload serves the verb, *No answer* where it did not answer in time.
 * Anything else — a refusal of who is asking, a failure with the service's
 * reason — is shown as every refusal is, and troubleOf is undefined.
 */
export function troubleOf(err: unknown): Trouble | undefined {
  if (isRefusal(err, CODE_NO_SERVICE)) {
    return {
      title: "Not running here",
      text: /does not run in this workspace/.test(err.message)
        ? err.message
        : "Nothing in this workspace answers this yet: the solution's workload is not running here.",
    };
  }
  if (isRefusal(err, CODE_NO_ANSWER)) {
    return {
      title: "No answer",
      text: "The solution did not answer in time. Open the panel again to retry.",
    };
  }
  return undefined;
}

/** A group is one element, or a list with the row actions that follow it. */
export interface Group {
  element: Element;
  rowActions: Element[];
}

/** grouped pairs every list with the actions after it that take a row's key. */
export function grouped(elements: readonly Element[]): Group[] {
  const out: Group[] = [];
  for (const e of elements) {
    const last = out.at(-1);
    if (
      e.kind === ElementKind.action &&
      e.row_key !== undefined &&
      last !== undefined &&
      last.element.kind === ElementKind.list
    ) {
      last.rowActions.push(e);
      continue;
    }
    out.push({ element: e, rowActions: [] });
  }
  return out;
}

/** Row is one row a list holds. */
export type Row = Record<string, unknown>;

function isRow(v: unknown): v is Row {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * withFrame folds one answer into a list's rows: an array is the rows whole,
 * replacing what was held; one object is one row, replacing the row with the
 * same key or added after the rest; an object with `deleted: true` removes it.
 */
export function withFrame(rows: readonly Row[], frame: unknown, key: string | undefined): Row[] {
  if (Array.isArray(frame)) {
    return frame.filter(isRow);
  }
  if (isRow(frame) && Array.isArray(frame.rows)) {
    return frame.rows.filter(isRow);
  }
  if (!isRow(frame)) {
    return [...rows];
  }
  const k = key === undefined ? undefined : frame[key];
  const at = k === undefined ? -1 : rows.findIndex((r) => r[key ?? ""] === k);
  if (frame.deleted === true) {
    return at < 0 ? [...rows] : rows.filter((_, i) => i !== at);
  }
  if (at < 0) {
    return [...rows, frame];
  }
  return rows.map((r, i) => (i === at ? frame : r));
}

/** Point is one point of a series: a time and a value. */
export interface Point {
  at: string;
  value: number;
}

/** pointsOf reads a series from an answer: an array of points, or `{points: […]}`; anything else is no points. */
export function pointsOf(frame: unknown): Point[] {
  const list: unknown = isRow(frame) && Array.isArray(frame.points) ? frame.points : frame;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: Point[] = [];
  for (const p of list) {
    if (isRow(p) && typeof p.at === "string" && typeof p.value === "number" && Number.isFinite(p.value)) {
      out.push({ at: p.at, value: p.value });
    }
  }
  return out;
}

/** stateOf reads a status from an answer: the string itself, or its `state` field. */
export function stateOf(frame: unknown): string | undefined {
  if (typeof frame === "string") {
    return frame;
  }
  return isRow(frame) && typeof frame.state === "string" ? frame.state : undefined;
}

/** keyOf is a row's key as the token a record or an action takes, or undefined when it is not one. */
export function keyOf(row: Row, key: string | undefined): string | undefined {
  if (key === undefined) {
    return undefined;
  }
  const v = row[key];
  if (typeof v !== "string" && typeof v !== "number") {
    return undefined;
  }
  const s = String(v);
  return /^[\x21-\x7e]+$/.test(s) && !/[.*>]/.test(s) ? s : undefined;
}

/**
 * detailKey is the key a row opens its detail on: the row's key, where the
 * list declares a record and the key is a token; undefined where the row
 * opens nothing (§ the elements: a row's detail).
 */
export function detailKey(e: Element, row: Row): string | undefined {
  return e.record === undefined ? undefined : keyOf(row, e.key);
}

/** opensOn reports whether a key pressed on a focused row opens it, as a button opens on Enter and Space. */
export function opensOn(key: string): boolean {
  return key === "Enter" || key === " ";
}

function held(v: unknown): boolean {
  return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
}

/**
 * heldFields is a record's declared fields the answer holds, in the declared
 * order: a field the answer does not hold is left out rather than drawn
 * empty, so one declaration serves records of different shapes.
 */
export function heldFields<F extends Pick<Field, "name">>(fields: readonly F[], row: Row): F[] {
  return fields.filter((f) => held(row[f.name]));
}

/**
 * heldColumns is a list's declared columns that some row holds, in the
 * declared order: a column no row holds is left out, as a record's field the
 * answer does not hold is (14-solutions/03 § the elements), so that a
 * column of a value only some answers carry — a cost a provider reported —
 * is drawn only where one is. With no rows, every declared column.
 */
export function heldColumns<F extends Pick<Field, "name">>(cols: readonly F[], rows: readonly Row[]): F[] {
  if (rows.length === 0) {
    return [...cols];
  }
  return cols.filter((c) => rows.some((r) => held(r[c.name])));
}

/**
 * peakLine is the chart's legend: the last point and the peak of the points
 * shown, or that the window holds nothing, rather than the scale's floor.
 */
export function peakLine(
  points: readonly { value: number }[],
  unit: string | undefined,
  lastAt: string,
): string {
  const u = unit ?? "";
  const peak = Math.max(0, ...points.map((p) => p.value));
  if (peak === 0) {
    return u === "" ? "nothing in this window" : `no ${u} in this window`;
  }
  const last = points.at(-1)?.value ?? 0;
  return `${String(last)} ${u} at ${lastAt} · peak ${String(peak)} ${u}`.replace(/ {2,}/g, " ");
}

/** cellItems is a value as the items it is drawn as: a list's items in order, anything else one item. */
export function cellItems(v: unknown): string[] {
  if (v === undefined || v === null) {
    return [];
  }
  const one = (x: unknown) => (typeof x === "string" ? x : JSON.stringify(x));
  return Array.isArray(v) ? v.map(one) : [one(v)];
}

/**
 * recordList is the list of a panel whose row opens a record's page at
 * `<entry>/<panel>/<key>` (14-solutions/03 § the elements; 01-the-console.md
 * § the address): the first list the panel declares with a **record**. The
 * address names the panel and the key, not the list, so a panel's second list
 * with a record of its own opens no page; one list per panel holds records in
 * every declaration the console knows.
 */
export function recordList(elements: readonly Element[]): Element | undefined {
  return elements.find((e) => e.kind === ElementKind.list && e.record !== undefined);
}

/** formKey is the name a page's form state gives a declared form: its role and verb, which no two forms of a panel share. */
export function formKey(e: Element): string {
  return `declared:${e.role ?? ""}.${e.verb ?? ""}`;
}

/** openForm is the declared form among a panel's elements that a page's form state names, if any. */
export function openForm(open: string | undefined, elements: readonly Element[]): Element | undefined {
  if (open === undefined) {
    return undefined;
  }
  return elements.find((e) => e.kind === ElementKind.form && formKey(e) === open);
}

/**
 * validRecordKey says whether a key from the address can name a record: one
 * subject token, as the record's verb takes it after itself,
 * `workload.<id>.<verb>.<key>`. A key that is not one is no record, said on
 * the page, and never sent.
 */
export function validRecordKey(key: string): boolean {
  return key !== "" && subject.validToken(key);
}

/**
 * opensOnFirstCell says whether a declared list's row opens on its first
 * cell, which is then the row's own link or button and holds no link of its
 * own: a row with a record's page to open — a list with a record whose row
 * holds the key — or a row that selects. A list that does neither leaves
 * its first cell's name free to link to the thing's page.
 */
export function opensOnFirstCell(
  opensRecord: boolean,
  selects: boolean,
  rowKey: string | undefined,
): boolean {
  return opensRecord ? rowKey !== undefined : selects;
}
